import React from "react";
import { InspectionQuestion } from "../types/inspectionWorkflow";
import { InspectionResult, ObjectPresenceCheck } from "src/services/inspectionService";
import { GroupedResultEntry, isAnalysisPending } from "../utils/resultGrouping";
import { isResultNA } from "src/components/pages/inspection/utils";
import ReviewCheckpointCard from "./ReviewCheckpointCard";
import ObjectPresenceCheckpointCard from "./ObjectPresenceCheckpointCard";

interface CheckpointListProps {
    questionEntries: GroupedResultEntry[];
    questions: InspectionQuestion[];
    presignedUrlMap: Record<string, string>;
    /** When omitted the list is read-only (resume-draft page). When present, cards get override affordances (review page). */
    highlightedQuestionId?: string | null;
    override?: {
        onResult: (result: InspectionResult, questionId: string) => void;
        onObjectCheck: (result: InspectionResult, questionId: string, check: ObjectPresenceCheck) => void;
        onCount: (result: InspectionResult, questionId: string) => void;
    };
}

const CheckpointList: React.FC<CheckpointListProps> = ({
    questionEntries,
    questions,
    presignedUrlMap,
    highlightedQuestionId,
    override,
}) => {
    const getQuestionText = (questionId: string): string => {
        const question = questions.find((q) => q.id === questionId);
        return question?.text || questionId;
    };

    return (
        <>
            {questionEntries.map(([questionId, results], idx) => {
                const firstResult = results[0];
                //  object presence checks related questions
                //  are expected to have exactly one result
                const hasObjectPresenceChecks =
                    firstResult.objectPresenceChecks !== null &&
                    firstResult.objectPresenceChecks !== undefined &&
                    firstResult.objectPresenceChecks.length > 0;

                const questionNumber = String(idx + 1).padStart(2, "0");

                let card: React.ReactNode;
                if (hasObjectPresenceChecks) {
                    const objectChecks = firstResult.objectPresenceChecks!;
                    const allCompliant = objectChecks.every((check) => check.is_compliant);

                    card = (
                        <ObjectPresenceCheckpointCard
                            onOverride={
                                override
                                    ? (check) => override.onObjectCheck(firstResult, questionId, check)
                                    : undefined
                            }
                            questionNumber={questionNumber}
                            questionText={getQuestionText(questionId)}
                            allCompliant={allCompliant}
                            objectChecks={objectChecks}
                            evidence={firstResult.evidence}
                            presignedUrlMap={presignedUrlMap}
                        />
                    );
                } else {
                    // container based questions could have multiple results
                    // depending on the number of containers recorded
                    const isNAQuestion = results.every((r) => isResultNA(r));
                    const scoredResults = results.filter((r) => !isResultNA(r));
                    const allCompliant = isNAQuestion || (scoredResults.length === 0 || scoredResults.every((r) => r.isCompliant));
                    const isAI =
                        firstResult?.aiReasoning !== null &&
                        firstResult?.aiReasoning !== "";
                    const isAutoCalculated = questions.find((q) => q.id === questionId)?.mode === "auto-calculated";

                    // Auto-calculated results (container count) override to a numeric
                    // count via a dedicated page; everything else uses the compliance override.
                    const onOverride = override
                        ? isAutoCalculated
                            ? (result: InspectionResult) => override.onCount(result, questionId)
                            : (result: InspectionResult) => override.onResult(result, questionId)
                        : undefined;

                    card = (
                        <ReviewCheckpointCard
                            onOverride={onOverride}
                            questionNumber={questionNumber}
                            questionText={getQuestionText(questionId)}
                            isAI={isAI}
                            isAutoCalculated={isAutoCalculated}
                            allCompliant={allCompliant}
                            isNA={isNAQuestion}
                            isAnalysisPending={results.some(isAnalysisPending)}
                            results={results}
                            presignedUrlMap={presignedUrlMap}
                        />
                    );
                }

                return (
                    <div
                        key={questionId}
                        id={`question-${questionId}`}
                        className={
                            highlightedQuestionId !== undefined
                                ? highlightedQuestionId === questionId
                                    ? "question-anchor question-anchor--highlight"
                                    : "question-anchor"
                                : undefined
                        }
                    >
                        {card}
                    </div>
                );
            })}
        </>
    );
};

export default CheckpointList;
